import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { t, pick } from "@/i18n";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, ArrowRight, ArrowLeft, Users, Lock, Crown } from "lucide-react";
import { Link } from "react-router-dom";
import { awardPoints } from "@/lib/points";

const STEP_KEYS = ["observation", "feeling", "need", "request", "empathy"];
const CONTEXT_KEYS = ["family", "partner", "work", "school", "relationships"];

export default function Exercises() {
  const { lang } = useLang();
  const [params, setParams] = useSearchParams();
  const stepId = params.get("step") || "all";
  const [exercises, setExercises] = useState([]);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [showAll, setShowAll] = useState(false); // review-mode toggle

  const loadExercises = useCallback(async () => {
    const qs = new URLSearchParams();
    if (stepId && stepId !== "all") qs.set("step_id", stepId);
    qs.set("exclude_done", showAll ? "false" : "true");
    try {
      const r = await api.get(`/content/exercises?${qs.toString()}`);
      setExercises(r.data);
      setIdx(0);
      setSelected(null);
      setRevealed(false);
    } catch (e) {
      console.error("failed to load exercises", e);
    }
  }, [stepId, showAll]);

  useEffect(() => {
    loadExercises();
  }, [loadExercises]);

  const ex = exercises[idx];

  const submit = async (option, i) => {
    if (ex?.locked) return;
    setSelected(i);
    setRevealed(true);
    try {
      const r = await api.post("/progress/exercise", { exercise_id: ex.id, correct: option.correct });
      awardPoints(r.data);
    } catch (e) {
      console.warn("exercise submit failed", e?.message || e);
    }
    if (option.correct) toast.success(t(lang, "correct"));
    else toast.error(t(lang, "incorrect"));
  };

  const goNext = () => {
    if (idx >= exercises.length - 1) {
      // Reload to fetch fresh un-done exercises (or an empty list if all done)
      loadExercises();
      return;
    }
    setIdx((i) => i + 1);
    setSelected(null);
    setRevealed(false);
  };
  const goPrev = () => {
    setIdx((i) => Math.max(i - 1, 0));
    setSelected(null);
    setRevealed(false);
  };

  const setStep = (s) => {
    setParams(s === "all" ? {} : { step: s });
  };

  return (
    <div className="space-y-8">
      <div className="text-xs uppercase tracking-widest text-accent font-medium">{t(lang, "exercises")}</div>

      {/* Step filter */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setStep("all")}
          data-testid="ex-filter-all"
          className={`px-4 py-2 rounded-full text-sm border transition-colors ${
            stepId === "all" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"
          }`}
        >
          {t(lang, "all_steps")}
        </button>
        {STEP_KEYS.map((s) => (
          <button
            key={s}
            onClick={() => setStep(s)}
            data-testid={`ex-filter-${s}`}
            className={`px-4 py-2 rounded-full text-sm border transition-colors ${
              stepId === s ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"
            }`}
          >
            {t(lang, s)}
          </button>
        ))}
        <div className="w-full h-0" />
        {CONTEXT_KEYS.map((c) => (
          <button
            key={c}
            onClick={() => setStep(c)}
            data-testid={`ex-filter-${c}`}
            className={`px-4 py-2 rounded-full text-sm border transition-colors flex items-center gap-1 ${
              stepId === c ? "bg-accent text-accent-foreground border-accent" : "border-accent/40 text-accent hover:bg-accent/10"
            }`}
          >
            <Crown className="w-3 h-3" strokeWidth={2} />
            {t(lang, c) || c}
          </button>
        ))}
      </div>

      {/* Review-mode toggle */}
      <div className="flex items-center justify-end">
        <button
          onClick={() => setShowAll((v) => !v)}
          data-testid="ex-review-toggle"
          className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
        >
          {showAll
            ? (t(lang, "hide_completed") || "Hide completed")
            : (t(lang, "review_completed") || "Review completed exercises")}
        </button>
      </div>

      {!ex ? (
        exercises.length === 0 ? (
          <div className="p-12 rounded-2xl border-2 border-dashed border-primary/40 bg-primary/5 text-center space-y-4" data-testid="exercise-empty">
            <div className="text-5xl">🎉</div>
            <h2 className="font-heading text-2xl font-medium">
              {t(lang, "exercises_completed_title") || "You've completed all exercises here!"}
            </h2>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              {t(lang, "exercises_completed_body") ||
                "Try another section from the filters above, or review the ones you've done."}
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap pt-2">
              {stepId !== "all" && (
                <Button
                  className="rounded-full gap-2"
                  onClick={() => setStep("all")}
                  data-testid="ex-empty-all-btn"
                >
                  {t(lang, "see_all_exercises") || "Ver otros ejercicios"}
                </Button>
              )}
              <Button
                variant="outline"
                className="rounded-full gap-2"
                onClick={() => setShowAll(true)}
                data-testid="ex-empty-review-btn"
              >
                {t(lang, "review_completed") || "Review completed"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-12 rounded-2xl border border-dashed border-border text-center text-muted-foreground">Loading…</div>
        )
      ) : (
        <>
          <div className="flex items-center justify-between">
            <h1 className="font-heading text-3xl md:text-4xl font-medium">
              {idx + 1} / {exercises.length}
            </h1>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              {ex.step_id === "empathy" && <Users className="w-4 h-4" />}
              <span className="capitalize">{t(lang, ex.step_id) || ex.step_id}</span>
            </div>
          </div>

          {ex.locked ? (
            <div className="p-10 rounded-2xl border-2 border-dashed border-accent/40 bg-accent/5 text-center space-y-4" data-testid="exercise-locked">
              <div className="w-14 h-14 rounded-2xl bg-accent text-accent-foreground flex items-center justify-center mx-auto">
                <Lock className="w-6 h-6" strokeWidth={1.5} />
              </div>
              <h2 className="font-heading text-2xl font-medium">{t(lang, "locked_content")}</h2>
              <p className="text-muted-foreground max-w-md mx-auto">{t(lang, "upgrade_to_unlock")}</p>
              <Link to="/app/plan">
                <Button className="rounded-full gap-2" data-testid="ex-upgrade-btn">
                  <Crown className="w-4 h-4" /> {t(lang, "upgrade")}
                </Button>
              </Link>
            </div>
          ) : (
          <div className="p-8 rounded-2xl border border-border bg-card space-y-6" data-testid="exercise-card">
            <p className="text-lg leading-relaxed">{pick(ex.prompt, lang)}</p>

            <div className="space-y-3">
              {ex.options.map((opt, i) => {
                const isSel = selected === i;
                const showCorrect = revealed && opt.correct;
                const showWrong = revealed && isSel && !opt.correct;
                return (
                  <button
                    key={`${ex.id}-opt-${i}`}
                    data-testid={`exercise-option-${i}`}
                    disabled={revealed}
                    onClick={() => submit(opt, i)}
                    className={`w-full text-left p-4 rounded-lg border transition-colors flex items-center justify-between gap-4 ${
                      showCorrect ? "border-primary bg-primary/10" :
                      showWrong ? "border-destructive bg-destructive/10" :
                      isSel ? "border-primary" : "border-border hover:bg-secondary/50"
                    }`}
                  >
                    <span>{pick(opt.text, lang)}</span>
                    {showCorrect && <CheckCircle2 className="w-5 h-5 text-primary" />}
                    {showWrong && <XCircle className="w-5 h-5 text-destructive" />}
                  </button>
                );
              })}
            </div>

            {revealed && (
              <div className="p-4 rounded-lg bg-secondary/70 border border-border">
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{t(lang, "show_explanation")}</div>
                <p>{pick(ex.explanation, lang)}</p>
              </div>
            )}

            <div className="flex justify-between">
              <Button variant="ghost" onClick={goPrev} disabled={idx === 0} className="rounded-full gap-2" data-testid="exercise-prev">
                <ArrowLeft className="w-4 h-4" /> {t(lang, "prev_exercise")}
              </Button>
              <Button onClick={goNext} className="rounded-full gap-2" data-testid="exercise-next">
                {t(lang, "next_exercise")} <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
          )}
          {ex.locked && (
            <div className="flex justify-between">
              <Button variant="ghost" onClick={goPrev} disabled={idx === 0} className="rounded-full gap-2" data-testid="exercise-prev">
                <ArrowLeft className="w-4 h-4" /> {t(lang, "prev_exercise")}
              </Button>
              <Button onClick={goNext} className="rounded-full gap-2" data-testid="exercise-next">
                {t(lang, "next_exercise")} <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
